import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../../hooks/useAccess';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useUser } from '../../hooks/useUser';
import { IUserRequest } from '../../model/IUser';
import { msg } from '../../msg';
import { getVendors } from '../../store/commonSlice';
import { IZodErrorsState } from '../../utils/checkFormStateByZod';
import AgentRoleMultiSearchField from '../agent/AgentRoleMultiSearchField';
import Button from '../form/Button';
import FormBlock from '../form/FormBlock';
import Input, { TInputEvent } from '../form/Input';
import Switch from '../form/Switch';
import UserRoleMultiSearchField, { initRolesState } from '../user/UserRoleMultiSearchField';
import VendorRoleMultiSearchField from '../vendor/VendorRoleMultiSearchField';
import VendorSelect from '../vendor/VendorSelect';

const UserForm: FC = () => {
  const { addUser } = useUser();
  const navigate = useNavigate();
  const vendorList = useTypedSelector(getVendors());

  // -- Check permissions
  const { getSettingAccess } = useAccess();
  useEffect(() => {
    !getSettingAccess('add-user') && navigate('/users');
  }, []);

  // -- Init form state
  const initState: IUserRequest = {
    email: '',
    name: '',
    phone: '',
    isActive: true,
    isAllowedForTickets: false,
    roles: initRolesState,
    vendor: '',
    vendorRole: '',
    agentRole: '',
    password: '',
    passwordConfirm: '',
    sendInviteEMail: false,
    isSearchableSales: true,
    isSearchableTickets: true
  };

  // -- States
  const [userState, setFormState] = useState<IUserRequest>(initState);
  const [userErrState, setNewUserErrState] = useState<Partial<IZodErrorsState<IUserRequest>>>();
  const [isPending, setIsPending] = useState<boolean>(() => false);

  // -- Fill default vendor
  useEffect(() => {
    vendorList?.length && !userState?.vendor && setFormState((prevState) => ({
      ...prevState,
      vendor: String(vendorList?.[0]?.id) || '',
    }));
  }, [vendorList]);

  const handle = (key: Partial<keyof typeof initState>) => (e: TInputEvent) =>
    setFormState((prevState) => ({
      ...prevState,
      [key]: e?.target?.value || '',
    }));

  const handleCheckbox = (key: Partial<keyof typeof initState>) => () =>
    setFormState((prevState) => ({
      ...prevState,
      [key]: !!!prevState?.[key],
    }));

  const hendleAddUser = async () => {
    setIsPending(() => true);
    const errs = await addUser(userState);

    if (errs) {
      setNewUserErrState(() => errs);
      setIsPending(() => false);
      return;
    }
    toast.success(msg('user-create'));
    return navigate('/users');
  };

  return (
    <div className="w-full">

      <FormBlock
        title="User Accessibility"
        subTitle="Modify user options"
        className="bg-gray-50"
      >
        <Switch
          label="Active for Tickets"
          name="isAllowedForTickets"
          value={userState.isAllowedForTickets ?? false}
          handleToggle={handleCheckbox('isAllowedForTickets')}
          className='col-span-6 xl:col-span-3 justify-center'
          disabled={isPending}
          isNoLabelArea
        />
        <Switch
          label="Searchable On Sales Portal"
          name="isSearchableSales"
          value={userState.isSearchableSales ?? true}
          handleToggle={handleCheckbox('isSearchableSales')}
          className='col-span-6 xl:col-span-3 justify-center'
          disabled={isPending}
          isNoLabelArea
        />
        <Switch
          label="Searchable On CS Portal"
          name="isSearchableTickets"
          value={userState.isSearchableTickets ?? true}
          handleToggle={handleCheckbox('isSearchableTickets')}
          className='col-span-6 xl:col-span-3 justify-center'
          disabled={isPending}
          isNoLabelArea
        />
      </FormBlock>

      <FormBlock
        title="User info"
        subTitle="Main User Info"
        className=""
      >
        <Input
          label="Email (required)"
          name="email"
          placeholder='Manager email'
          value={userState.email}
          handle={handle('email')}
          className='col-span-6 md:col-span-6'
          error={userErrState?.email}
          disabled={isPending}
        />
        <Switch
          label="Enable"
          name="active"
          value={userState.isActive}
          handleToggle={handleCheckbox('isActive')}
          className='col-span-3 justify-center'
          disabled={isPending}
        />
        <Switch
          label="Send invite"
          name="active"
          value={userState.sendInviteEMail}
          handleToggle={handleCheckbox('sendInviteEMail')}
          className='col-span-3 justify-center'
          disabled={isPending}
        />
        <Input
          label="Name"
          name="name"
          placeholder='Manager name'
          value={userState.name}
          handle={handle('name')}
          className='col-span-6'
          error={userErrState?.name}
          disabled={isPending}
        />
        <Input
          label="Phone"
          name="phone"
          placeholder='Manager phone'
          value={userState.phone}
          handle={handle('phone')}
          className='col-span-6'
          error={userErrState?.phone}
          disabled={isPending}
        />
        <Input
          label='Password (required)'
          name="password"
          value={userState.password}
          handle={handle('password')}
          type="password"
          className='col-span-6'
          error={userErrState?.password}
          disabled={isPending}
        />
        <Input
          label='Confirm Password (required)'
          name="passwordConformed"
          value={userState.passwordConfirm}
          handle={handle('passwordConfirm')}
          type="password"
          className='col-span-6'
          error={userErrState?.passwordConfirm}
          disabled={isPending}
        />
      </FormBlock>

      <FormBlock
        title="Roles"
        subTitle="User Roles Info"
        className="bg-gray-100"
      >
        <UserRoleMultiSearchField
          label="Roles"
          name="roles"
          placeholder='User role'
          value={userState.roles}
          handle={handle('roles')}
          className='col-span-12'
          error={userErrState?.roles}
          disabled={isPending}
        />
        {userState.roles.includes('ROLE_VENDOR') && (
          <>
            <VendorSelect
              label="Vendor"
              name="vendor"
              placeholder='Attach to Vendor'
              value={userState.vendor}
              handle={handle('vendor')}
              error={userErrState?.vendor}
              disabled={isPending}
              className='col-span-6'
            />
            {!!userState.vendor && (
              <VendorRoleMultiSearchField
                label="Vendor Role"
                name="vendorRole"
                placeholder='Role for Vendor'
                value={userState.vendorRole}
                handle={handle('vendorRole')}
                className='col-span-6'
                error={userErrState?.vendorRole}
                disabled={isPending}
              />
            )}
          </>
        )}

        {userState.roles.includes('ROLE_AGENT') && (
          <div className='col-span-12'>
            <AgentRoleMultiSearchField
              label="Agent Role"
              name="agentRole"
              placeholder='Role for Agent'
              value={userState.agentRole}
              handle={handle('agentRole')}
              error={userErrState?.agentRole}
              disabled={isPending}
            />
          </div>
        )}
      </FormBlock>

      <div className="flex justify-end gap-4 mt-6 col-span-12 p-4 ">
        <Button
          onClick={() => setFormState(() => initState)}
          variant="outline"
          isNotFullWidth
          disabled={isPending}
        >
          Reset From
        </Button>
        <Button
          type="submit"
          onClick={() => hendleAddUser()}
          isNotFullWidth
          disabled={isPending}
        >
          Add User
        </Button>
      </div>
    </div>
  );
}

export default UserForm;
